@use '../scss/abstracts/variables' as *;

.page {
  background: #f6f1e7;
}

// ---------- Hero ----------
.hero {
  position: relative;
  min-height: 44rem;
  display: flex;
  align-items: center;
  background:
    linear-gradient(
      90deg,
      rgba(20, 15, 9, 0.86),
      rgba(20, 15, 9, 0.45) 60%,
      rgba(20, 15, 9, 0.55)
    ),
    #2a241b;
  color: #f3ecdf;
  overflow: hidden;
  text-align: center;
  justify-content: center;

  @media screen and (max-width: $tablet-portrait) {
    min-height: 36rem;
  }
}

.heroCarousel {
  position: absolute;
  inset: 0;
  z-index: 0;

  :global(.carousel),
  :global(.carousel-inner),
  :global(.carousel-item) {
    height: 100%;
  }

  :global(.carousel-control-prev),
  :global(.carousel-control-next) {
    width: 8%;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 3;
  }

  &:hover {
    :global(.carousel-control-prev),
    :global(.carousel-control-next) {
      opacity: 1;
    }
  }
}

.heroImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.heroScrim {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    90deg,
    rgba(20, 15, 9, 0.86),
    rgba(20, 15, 9, 0.45) 60%,
    rgba(20, 15, 9, 0.55)
  );
}

.heroIn {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 90rem;
  margin: 0 auto;
  padding: 7.4rem 4rem;
}

.eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: #bf8a2e;
}

.title {
  font-family: 'Playfair Display', serif;
  font-size: 3.8rem;
  line-height: 1.1;
  font-weight: 600;
  color: #f6f0e4;
  margin: 1.4rem 0 1.6rem;

  em,
  i {
    font-style: italic;
    color: #c9a44d;
  }

  @media screen and (max-width: $tablet-portrait) {
    font-size: 2.6rem;
  }
}

.lede {
  max-width: 46em;
  margin: 0 auto;
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-size: 1.6rem;
  line-height: 1.6;
  color: #e2d7c2;
}

// ---------- Content ----------
.content {
  max-width: 118rem;
  margin: 0 auto;
  padding: 6rem 4rem 4rem;

  @media screen and (max-width: $tablet-portrait) {
    padding: 4rem 3.6rem 2rem;
  }
}

.empty {
  font-family: 'Inter', sans-serif;
  font-size: 1.3rem;
  color: #6d6655;
  text-align: center;
  padding: 4rem 0;
}

// ---------- Layout ----------
.layout {
  display: grid;
  grid-template-columns: 23rem minmax(0, 1fr);
  gap: 6rem;
  align-items: start;

  @media screen and (max-width: $tablet-landscape) {
    grid-template-columns: 1fr;
  }
}

.articles {
  max-width: 84rem;
  min-width: 0;
}

// ---------- Side nav ----------
.sideNav {
  position: sticky;
  top: 11rem;
  max-height: calc(100vh - 13rem);
  overflow-y: auto;

  @media screen and (max-width: $tablet-landscape) {
    display: none;
  }
}

.sideNavTitle {
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a8270;
  margin-bottom: 1.4rem;
}

.sideNavList {
  list-style: none;
  margin: 0;
  padding: 0;
  border-left: 1px solid rgba(43, 40, 32, 0.14);
}

.sideNavLink {
  display: flex;
  align-items: baseline;
  gap: 0.8rem;
  padding: 0.8rem 0 0.8rem 1.4rem;
  margin-left: -1px;
  border-left: 2px solid transparent;
  font-family: 'Inter', sans-serif;
  font-size: 1.15rem;
  line-height: 1.35;
  color: #6d6655;
  text-decoration: none;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: #2b2820;
  }
}

.sideNavNum {
  flex: 0 0 auto;
  font-weight: 700;
  color: #bf8a2e;
}

.sideNavLinkActive {
  color: #2b2820;
  font-weight: 600;
  border-left-color: #bf8a2e;
}

// ---------- Sections ----------
.section {
  // Anchor-scrolling (side-nav links, browser back/forward) targets this
  // element directly — offset by the sticky header's height so the
  // section-num/heading land in view instead of hiding underneath it.
  scroll-margin-top: 12rem;
  padding: 3.2rem;

  // Alternating cream bands (same pair used across accommodation/featured
  // properties) instead of a plain divider line between sections.
  &:nth-of-type(odd) {
    background: #efe7d7;
  }

  &:nth-of-type(even) {
    background: #f4eee2;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: $tablet-portrait) {
    padding: 2.2rem 1.8rem;
  }
}

.sectionNum {
  font-family: 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a8270;
  margin-bottom: 1.2rem;
}

.standaloneLink {
  display: inline-block;
  margin-top: 2rem;
  font-family: 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: #bf8a2e;
  text-decoration: none;

  &:hover {
    color: #96692a;
  }
}
